import axios, { AxiosError } from 'axios';
import { Base64 } from 'js-base64';

import {
  IWalletTradeOverlay,
  IWalletInfoChartItem,
  IWallet,
  IActiveWallet,
  IWalletOverview,
  IWalletTradesCountAndVolume,
  IWalletTags,
  IUpdateWalletTagsPayload,
} from '../interfaces/wallets';
import { IDexTrade } from '../interfaces/common';
import { IDexEventOverlay } from '../interfaces/kattana';

export interface IWalletApi {
  overlayEndpoint: string;
  walletInfoEndpoint: string;
  walletTagsEndpoint: string;

  overlayWalletTrades: (
    wallet: string,
    network: string,
    pairAddress: string,
    from: number,
    to: number
  ) => Promise<IWalletTradeOverlay[] | null>;
  walletInfoChart: (
    chain: string,
    address: string,
    signal?: AbortSignal
  ) => Promise<IWalletInfoChartItem[] | null>;
  walletPeriodDexTrades: (
    chain: string,
    address: string,
    period: 'day' | 'week' | 'month',
    abortController?: AbortController
  ) => Promise<IDexTrade[] | null>;
  walletAllDexTrades: (
    chain: string,
    address: string,
    params: {
      before: number;
      size: number;
    },
    abortController?: AbortController
  ) => Promise<IDexTrade[] | null>;
  overlayDexEvents: (
    network: string,
    token: string,
    authToken?: string
  ) => Promise<IDexEventOverlay[] | null>;
  fetchOverlayLimitOrders: (
    wallet: string,
    network: string,
    pairAddress: string
  ) => Promise<any>;
  fetchOthersOverlayLimitOrders: (
    wallet: string,
    srcAddress: string,
    dstAddress: string,
    baseDecimals: number,
    quoteDecimals: number
  ) => Promise<any>;
  fetchTopWallets: (
    authToken?: string,
    limit?: number,
    controller?: AbortController
  ) => Promise<IWallet[] | null>;
  fetchActiveWallets: (
    chain: string,
    limit?: number,
    controller?: AbortController
  ) => Promise<IActiveWallet[] | null>;
  searchForWallets: (
    chain: string,
    search: string,
    limit?: number,
    controller?: AbortController
  ) => Promise<IWallet[] | null>;
  fetchWalletOverview: (
    chain: string,
    address: string,
    controller?: AbortController
  ) => Promise<IWalletOverview | null>;
  fetchTradesCountAndVolume: (
    chain: string,
    address: string,
    controller?: AbortController
  ) => Promise<IWalletTradesCountAndVolume | null>;
  fetchWalletTags: (
    authToken: string,
    controller?: AbortController
  ) => Promise<IWalletTags | null>;
  updateWalletTags: (
    payload: IUpdateWalletTagsPayload,
    authToken: string,
    controller?: AbortController
  ) => Promise<{
    success: boolean;
    error?: AxiosError;
  }>;
}

export class WalletApi implements IWalletApi {
  overlayEndpoint: string;
  walletInfoEndpoint: string;
  walletTagsEndpoint: string;

  constructor(apiGateway: string) {
    if (!apiGateway) {
      apiGateway = 'https://api.kattana.trade';
    }

    this.overlayEndpoint = `${apiGateway}/overlays`;
    this.walletInfoEndpoint = `${apiGateway}/wallet-info`;
    this.walletTagsEndpoint = `${apiGateway}/wallet-tags`;
  }

  /**
   * Fetch UI info
   * @returns
   */
  public overlayWalletTrades = async (
    wallet: string,
    network: string,
    pairAddress: string,
    from: number,
    to: number
  ): Promise<IWalletTradeOverlay[] | null> => {
    try {
      const res = await axios(
        `${this.overlayEndpoint}/wallet_trades/${wallet}/${network}/${pairAddress}`,
        {
          params: {
            from,
            to,
          },
        }
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public walletInfoChart = async (
    chain: string,
    address: string,
    signal?: AbortSignal
  ): Promise<IWalletInfoChartItem[] | null> => {
    try {
      const res = await axios.get(
        `${this.walletInfoEndpoint}/${chain}/${address}`,
        {
          signal,
        }
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public walletPeriodDexTrades = async (
    chain: string,
    address: string,
    period: 'day' | 'week' | 'month',
    abortController?: AbortController
  ): Promise<IDexTrade[] | null> => {
    try {
      const res = await axios.get(
        `${this.walletInfoEndpoint}/trades/${chain}/${address}`,
        {
          params: { period },
          signal: abortController?.signal,
        }
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public walletAllDexTrades = async (
    chain: string,
    address: string,
    params: {
      before: number;
      size: number;
    },
    abortController?: AbortController
  ): Promise<IDexTrade[] | null> => {
    try {
      const res = await axios.get(
        `${this.walletInfoEndpoint}/all-trades/${chain}/${address}`,
        {
          params,
          signal: abortController?.signal,
        }
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public overlayDexEvents = async (
    network: string,
    token: string,
    authToken?: string
  ): Promise<IDexEventOverlay[] | null> => {
    try {
      const config = authToken
        ? {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        : undefined;
      const res = await axios(
        `${this.overlayEndpoint}/dex/events/${network}/${token}`,
        config
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public fetchOverlayLimitOrders = async (
    wallet: string,
    network: string,
    pairAddress: string
  ): Promise<any> => {
    try {
      const res = await axios(
        `${this.overlayEndpoint}/limit_orders/${wallet}/${network}/${pairAddress}`
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  public fetchOthersOverlayLimitOrders = async (
    wallet: string,
    srcAddress: string,
    dstAddress: string,
    baseDecimals: number,
    quoteDecimals: number
  ): Promise<any> => {
    try {
      const res = await axios(
        `${this.overlayEndpoint}/other_limits/${wallet}/${srcAddress}/${dstAddress}/${baseDecimals}/${quoteDecimals}`
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  public fetchTopWallets = async (
    authToken?: string,
    limit?: number,
    controller?: AbortController
  ): Promise<IWallet[] | null> => {
    try {
      const headers = authToken
        ? {
            Authorization: `Bearer ${authToken}`,
          }
        : undefined;
      const url = `${this.walletInfoEndpoint}/top-wallets`;

      const { data } = await axios.get(url, {
        params: { limit },
        signal: controller?.signal,
        headers,
      });
      return data;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  public fetchActiveWallets = async (
    chain: string,
    limit?: number,
    controller?: AbortController
  ): Promise<IActiveWallet[] | null> => {
    try {
      const url = `${this.walletInfoEndpoint}/most-active/${chain}`;

      const { data } = await axios.get(url, {
        params: { limit },
        signal: controller?.signal,
      });
      return data;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  public searchForWallets = async (
    chain: string,
    search: string,
    limit?: number,
    controller?: AbortController
  ): Promise<IWallet[] | null> => {
    try {
      const url = `${this.walletInfoEndpoint}/wallets`;

      const params = {
        network: chain,
        search: Base64.encodeURI(search),
        limit,
      };

      const { data } = await axios.get(url, {
        params,
        signal: controller?.signal,
      });
      return data;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  public fetchWalletOverview = async (
    chain: string,
    address: string,
    controller?: AbortController
  ): Promise<IWalletOverview | null> => {
    try {
      const url = `${this.walletInfoEndpoint}/overview/${chain}/${address}`;

      const { data } = await axios.get(url, { signal: controller?.signal });
      return data;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  public fetchTradesCountAndVolume = async (
    chain: string,
    address: string,
    controller?: AbortController
  ): Promise<IWalletTradesCountAndVolume | null> => {
    try {
      const url = `${this.walletInfoEndpoint}/trades-count-volume/${chain}/${address}`;

      const { data } = await axios.get(url, { signal: controller?.signal });
      return data;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  public fetchWalletTags = async (
    authToken: string,
    controller?: AbortController
  ): Promise<IWalletTags | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        signal: controller?.signal,
      };
      const res = await axios.get(this.walletTagsEndpoint, config);
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public updateWalletTags = async (
    payload: IUpdateWalletTagsPayload,
    authToken: string,
    controller?: AbortController
  ): Promise<{
    success: boolean;
    error?: AxiosError;
  }> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        signal: controller?.signal,
      };

      await axios.post(this.walletTagsEndpoint, payload, config);
      return { success: true };
    } catch (err) {
      console.warn(err);
      if (axios.isAxiosError(err)) {
        return {
          success: false,
          error: err,
        };
      }
      return { success: false };
    }
  };
}
